import FJCanvasPool from '../FJCanvasPool';
import FJUtil from '../../util/FJUtil';
import FJCoreConfig from '../FJCoreConfig';
import { FJDecoderManager } from 'fj-codec-library';
import FJMathUtil from '../../util/FJMathUtil';
import FJVideoUtil from '../../util/FJVideoUtil';
import FJRenderUtil from '../../util/FJRenderUtil';
import { FJDecodeMode } from '@fj/codec';

const DECODER_STATUS = {
    IDLE: 'IDLE',
    DECODING: 'DECODING',
    DONE: 'DONE',
    ERROR: 'ERROR',
    CLEARED: 'CLEARED',
};

class FJVideoDecoder {
    /**
     * @desc
     * @param props
     */
    constructor(props) {
        this._id = props.id;
        this._videoDuration = props.videoDuration ? props.videoDuration : null;
        this._frameFillType = props.frameFillType || 'lastFrame'; // 补帧方式，lastFrame, redecode
        this._status = DECODER_STATUS.IDLE;
        this._frames = [];
        this._watchers = [];
        this._errMsg = null;
        this._frameIndex = 0; // 绑定帧索引
        this._decodedFrameCnt = 0; // 已解码帧数
        this._totalFrameCnt = 0; // 总帧数
        this._lastFrame = null;
        this._validFramesSizeResolve = null;
        this._metadataCallback = props.metadataCallback || null;
        this._hasAlpha = false;
    }

    get id() {
        return this._id;
    }

    get errMsg() {
        return this._errMsg;
    }

    /**
     * @desc 绑定帧索引
     * @return {number}
     */
    bindFrameIndex() {
        const idx = this._frameIndex;
        if (idx >= this._frames.length) {
            if (this._lastFrame) {
                this._frames.push(this._cloneLastFrame());
            } else if (this._status === DECODER_STATUS.DECODING) {
                this._frames.push(null);
                this._totalFrameCnt++;
            } else {
                const lastFrame = this._frames[this._frameIndex.length - 1];
                if (lastFrame) {
                    this._frames.push(this._cloneLastFrame());
                    this._totalFrameCnt++;
                } else {
                    const errMsg =
                        'Element frame index out of range. Status: ' +
                        (this._status === DECODER_STATUS.ERROR ? this._errMsg : this._status);
                    this._error('Video frame index out of range. Status: ' + errMsg + ', ' + this._exportMsg);
                    return -1;
                }
            }
        }

        this._frameIndex++;
        return idx;
    }

    /**
     * @desc 观察某一帧的解码
     * @param idx
     * @param watcher
     */
    watchFrameAtIndex(idx, watcher) {
        this._watchers[idx] = watcher;
        if (this._validFramesSizeResolve) {
            this._validFramesSizeResolve(this._getValidFramesSize());
            this._validFramesSizeResolve = null;
        }
    }

    /**
     * @desc 帧解码完成，响应观察者回调
     * @param idx
     * @param frame
     * @private
     */
    _addFrameAtIndex(idx, frame) {
        this._frames[idx] = frame;

        if (this._watchers[idx]) {
            this._watchers[idx]();
            this._watchers[idx] = null;
        }
    }

    /**
     * @desc 开始解码视频
     * @param source
     * @param startTime
     * @param endTime
     * @param exportSize
     * @param speed
     * @param elementDuration
     */
    startDecodeVideo(source, startTime, endTime, exportSize, speed, elementDuration) {
        if (!source) {
            this._error('Video source is null');
            return;
        }

        this._exportMsg =
            'Export parameters: ' +
            JSON.stringify({
                startTime: startTime,
                endTime: endTime,
                exportSize: exportSize,
                speed: speed,
            });
        this._status = DECODER_STATUS.DECODING;
        this._bufferCanvas = FJCanvasPool.getCanvas();

        endTime = FJMathUtil.toFixed(endTime, 2);

        this._totalFrameCnt = Math.ceil(elementDuration * FJCoreConfig.FPS);
        for (let i = 0; i < this._totalFrameCnt; i++) {
            // 先使用null占位
            this._frames.push(null);
        }

        const __decodeCallback = frame => {
            try {
                if (!this._isDecodingActive()) {
                    return;
                }

                const { data, error, ended } = frame;
                if (ended) {
                    // 当解码出来的帧不足时，需要继续渲染最后一帧的视频
                    if (this._decodedFrameCnt === 0) {
                        this._error('Decode done with none frame.');
                        return;
                    }

                    // 缓存最后一帧
                    this._cacheLastFrame();

                    // 如果帧不足且不存在最后一帧，则直接报错
                    if (this._decodedFrameCnt < this._totalFrameCnt) {
                        // 需要循环解码
                        if (this._frameFillType === 'redecode') {
                            if (endTime > this._videoDuration) {
                                endTime -= this._videoDuration;
                            }
                            __decodeVideo(startTime, endTime);
                        } else {
                            // 不需要循环解码，直接使用最后一帧
                            if (!this._lastFrame) {
                                this._error('No last frame.');
                                return;
                            }

                            for (let i = this._decodedFrameCnt; i < this._totalFrameCnt; i++) {
                                this._addFrameAtIndex(i, this._cloneLastFrame());
                            }
                            this._done();
                        }
                    } else {
                        this._done();
                    }
                    this._exportMsg += ' 帧数:' + this._decodedFrameCnt + '/' + this._totalFrameCnt;
                } else if (error) {
                    this._error(error?.message);
                } else if (data) {
                    // 防止解码器多返回帧
                    if (this._decodedFrameCnt <= this._totalFrameCnt) {
                        const { dst_width, dst_height, data: frameData, type: frameType, isLastFrame, rotate } = data;
                        const frameCanvas =
                            frameType === 'VIDEOFRAME' || frameType === 'IMAGEBITMAP'
                                ? frameData
                                : this._renderDecodeBufferToCanvas(frameData, {
                                      dst_width,
                                      dst_height,
                                      rotate,
                                      frameType,
                                  });

                        // 缓存最后一帧
                        if (isLastFrame || this._decodedFrameCnt === this._totalFrameCnt) {
                            this._releaseLastFrame();
                            this._cacheLastFrame(frameCanvas);
                        }

                        this._addFrameAtIndex(this._decodedFrameCnt, frameCanvas);
                        this._decodedFrameCnt++;
                    } else {
                        this._cacheLastFrame();
                        this._done();
                    }
                }
            } catch (e) {
                if (e?.message !== 'export canceled') {
                    this._error(e);
                }
            }
        };

        // 解码视频
        const __decodeVideo = (start, end) => {
            try {
                // 如果解码器已经被取消，则直接返回
                if (!this._isDecodingActive()) {
                    return;
                }

                const config = {
                    fileName: source.name,
                    width: exportSize.width,
                    height: exportSize.height,
                    start: start,
                    end: end,
                    speed: speed,
                    fps: FJCoreConfig.FPS,
                    samplerate: FJUtil.getAudioSampleRate(),
                    mode: FJDecodeMode.FFMPEG,
                    frameType: FJVideoUtil.getDecodeVideoFrameType(),
                    ignoreNoValidDuration: true,
                };

                FJDecoderManager.decodeVideo(source, config, {
                    decodeCallback: frame => {
                        __decodeCallback(frame);
                    },
                    metadataCallback: () => {},
                    getValidFramesSize: async () => {
                        const count = this._getValidFramesSize();
                        if (count === 0) {
                            return await new Promise(resolve => (this._validFramesSizeResolve = resolve));
                        }
                        return count;
                    },
                });
            } catch (e) {
                if (e?.message !== 'export canceled') {
                    this._error(e);
                }
            }
        };

        // 加载source
        (async () => {
            try {
                __decodeVideo(startTime, endTime);
            } catch (e) {
                this._error(e?.message);
            }
        })();
    }

    _releaseLastFrame() {
        if (this._lastFrame) {
            if (this._lastFrame.close) {
                this._lastFrame.close();
            } else {
                FJCanvasPool.releaseCanvas(this._lastFrame);
            }
            this._lastFrame = null;
        }
    }

    _releaseBufferCanavas() {
        if (this._bufferCanvas) {
            FJCanvasPool.releaseCanvas(this._bufferCanvas);
            this._bufferCanvas = null;
        }
    }

    _releaseDecodedCanvas() {
        this._frames.forEach(frame => frame?.data?.close());
        this._frames = [];
    }

    /**
     * @desc 缓存最后一帧
     * @private
     */
    _cacheLastFrame(frame) {
        if (this._lastFrame) return;
        const frameCanvas = frame || this._frames[this._decodedFrameCnt - 1];
        if (frameCanvas) {
            if (FJUtil.isSupportVideoFrame() && frameCanvas instanceof VideoFrame) {
                this._lastFrame = new VideoFrame(frameCanvas);
            } else if (FJUtil.isSupportImageBitmap() && frameCanvas instanceof ImageBitmap) {
                this._lastFrame = FJUtil.getImageBitmap(frameCanvas);
            } else {
                this._lastFrame = FJUtil.cloneCanvas(frameCanvas, null, true);
            }
        }
    }

    _cloneLastFrame() {
        if (!this._lastFrame) return;
        if (FJUtil.isSupportVideoFrame() && this._lastFrame instanceof VideoFrame) {
            return new VideoFrame(this._lastFrame);
        } else if (FJUtil.isSupportImageBitmap() && this._lastFrame instanceof ImageBitmap) {
            return FJUtil.getImageBitmap(this._lastFrame);
        } else {
            return FJUtil.cloneCanvas(this._lastFrame, null, true);
        }
    }

    /**
     * @desc 判断指定的帧是否是最后一帧
     * @param frame
     * @return {boolean}
     */
    isLastFrame(frame) {
        return this._lastFrame === frame;
    }

    /**
     * @desc 取消解码
     */
    cancel() {
        this.clear();
    }

    /**
     * @desc 清空解码器
     */
    clear() {
        if (this._status === DECODER_STATUS.CLEARED) return;
        this._status = DECODER_STATUS.CLEARED;

        this._watchers = [];

        // 释放所有缓存帧的canvas
        this._releaseDecodedCanvas();

        // 释放最后一帧的canvas
        this._releaseLastFrame();

        // 释放bufferCanvas
        this._releaseBufferCanavas();
    }

    /**
     * @desc 判断指定的帧是否准备好了
     * @param idx
     * @return {boolean}
     */
    framePreparedAtIndex(idx) {
        if (this._frames.length <= idx) return false;

        return !!this._frames[idx];
    }

    _getValidFramesSize() {
        // return Math.max(MAX_FRAME_CACHE - this._frames.filter(frame => !!frame).length, 0);
        return this._watchers.filter(frame => !!frame).length;
    }

    /**
     * @desc 获取指定帧的canvas
     * @param idx
     * @return {null|*}
     */
    getFrameAtIndex(idx) {
        if (this._frames.length <= idx) return null;

        const frame = this._frames[idx];
        this._frames[idx] = null;

        return frame;
    }

    /**
     * @desc 解码错误
     * @param msg
     * @private
     */
    _error(msg) {
        if (this._status !== DECODER_STATUS.DECODING) return;

        this._status = DECODER_STATUS.ERROR;
        this._errMsg = msg;

        const watcher = this._watchers.find(item => !!item);
        watcher && watcher(this._errMsg);
        this._watchers = [];
    }

    /**
     * @desc 解码完成
     * @private
     */
    _done() {
        if (this._status !== DECODER_STATUS.DECODING) return;

        this._status = DECODER_STATUS.DONE;

        // 释放bufferCanvas
        this._releaseBufferCanavas();
    }

    /**
     * @desc 判断当前解码是否有效
     * @return {boolean|*|HTMLCanvasElement}
     * @private
     */
    _isDecodingActive() {
        return this._status === DECODER_STATUS.DECODING && this._bufferCanvas;
    }

    /**
     * @desc 将解码出来的视频帧的buf渲染在canvas上
     * @param frame
     * @param frameInfo
     */
    _renderDecodeBufferToCanvas(frame, frameInfo) {
        const { dst_width, dst_height, rotate, frameType } = frameInfo;
        const imgWidth = rotate === 90 || rotate === 270 ? dst_height : dst_width;
        const imgHeight = rotate === 90 || rotate === 270 ? dst_width : dst_height;
        const canvas = FJCanvasPool.getCanvas();
        canvas.width = dst_width;
        canvas.height = dst_height;

        if (!FJUtil.isCanvasMatchFrameType(this._bufferCanvas, frameType)) {
            FJCanvasPool.releaseCanvas(this._bufferCanvas);
            this._bufferCanvas = FJCanvasPool.getCanvas(FJUtil.getCtxTypeWithFrameType(frameType));
        }

        // 将解码出来的视频帧的buf渲染在this._bufferCanvas上
        FJRenderUtil.renderDecodedFrameToCanvas(
            this._bufferCanvas,
            frame,
            { width: imgWidth, height: imgHeight },
            frameInfo,
        );

        // 将this._bufferCanvas渲染在canvas上，应用视频自带的旋转
        const exportCtx = canvas.getContext('2d');
        exportCtx.save();
        FJMathUtil.changeOrientation(
            exportCtx,
            {
                width: imgWidth,
                height: imgHeight,
            },
            rotate,
            1,
            1,
        );
        exportCtx.drawImage(this._bufferCanvas, 0, 0, imgWidth, imgHeight);
        exportCtx.restore();

        return canvas;
    }

    get hasAlpha() {
        return this._hasAlpha;
    }
}

export default FJVideoDecoder;
